<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { Toast } from '@/composables/useToast'
import { useToast } from '@/composables/useToast'
import AnimatedIcon from '@/components/common/AnimatedIcon.vue'

const props = defineProps<{
  toast: Toast
}>()

const { remove } = useToast()
const progress = ref(100)


const handleClose = () => {
  remove(props.toast.id)
}

onMounted(() => {
  if (props.toast.duration && props.toast.duration > 0) {
    const startTime = Date.now()
    const endTime = startTime + props.toast.duration
    
    const updateProgress = () => {
      const now = Date.now()
      const remaining = endTime - now
      const percentage = (remaining / props.toast.duration!) * 100
      
      progress.value = Math.max(0, percentage)
      
      if (remaining > 0) {
        requestAnimationFrame(updateProgress)
      }
    }
    
    requestAnimationFrame(updateProgress)
  }
})



const getIconColor = (type: string) => {
  switch (type) {
    case 'success': return 'var(--color-emerald-500)'
    case 'error': return 'var(--color-red-500)'
    case 'warning': return 'var(--color-amber-500)'
    case 'info': return 'var(--color-blue-500)'
    default: return 'var(--color-blue-500)'
  }
}
</script>

<template>
  <div 
    class="toast-item"
    :style="{ '--toast-c': getIconColor(toast.type) }"
    @click="handleClose"
  >
    <!-- Progress Bar -->
    <div 
      v-if="toast.duration" 
      class="progress-bar" 
      :style="{ width: `${progress}%`, backgroundColor: getIconColor(toast.type) }"
    ></div>

    <div class="toast-content">
      <div class="toast-icon-wrapper" :style="{ backgroundColor: `color-mix(in srgb, ${getIconColor(toast.type)} 8%, transparent)`, color: getIconColor(toast.type) }">
        <AnimatedIcon 
          :animation="toast.type === 'error' ? 'shake' : 'scale'" 
          trigger="always"
          :size="20"
          :color="getIconColor(toast.type)"
        >
          <!-- Success Icon -->
          <svg v-if="toast.type === 'success'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          <!-- Error Icon -->
          <svg v-else-if="toast.type === 'error'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <!-- Warning Icon -->
          <svg v-else-if="toast.type === 'warning'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008v.008H12v-.008z" />
          </svg>
          <!-- Info Icon -->
          <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
        </AnimatedIcon>
      </div>
      
      <div class="toast-text">
        <h4 v-if="toast.title" class="toast-title">{{ toast.title }}</h4>
        <p class="toast-message">{{ toast.message }}</p>
      </div>

      <button class="close-btn" @click.stop="handleClose">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.toast-item {
  width: 400px;
  background: var(--color-bg-primary);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--sp-16);
  box-shadow: 
    var(--shadow-lg);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all calc(0.3s * var(--motion-scale)) cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
}

.toast-item:hover {
  transform: translateY(-2px) scale(1.01);
  box-shadow: 
    var(--shadow-lg);
  background: var(--color-bg-primary);
  border-color: color-mix(in srgb, var(--color-white) 12%, transparent);
}

.progress-bar {
  position: absolute;
  bottom: 0px;
  left: 0;
  height: 2px;
  transition: width calc(0.1s * var(--motion-scale)) linear;
  opacity: 0.8;
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}

.toast-content {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-16);
}

.toast-icon-wrapper {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform calc(0.3s * var(--motion-scale)) ease;
}

.toast-item:hover .toast-icon-wrapper {
  transform: scale(1.1);
}

.toast-text {
  flex: 1;
  padding-top: var(--sp-2);
}

.toast-title {
  color: var(--color-text-primary);
  font-size: calc(15px * var(--type-scale));
  font-weight: 600;
  margin: 0 0 var(--sp-4) 0;
  letter-spacing: -0.01em;
}

.toast-message {
  color: var(--color-gray-400);
  font-size: calc(14px * var(--type-scale));
  margin: 0;
  line-height: 1.5;
  /* Güvenlik ağı: uzun içerik (HTML gövdesi, upuzun sohbet mesajı) toast'ı ekran
     boyu büyütmesin — 16 Eyl 2026'da ham HTML bildirimi ekranı kaplamıştı. */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.close-btn {
  background: transparent;
  border: none;
  color: var(--color-gray-500);
  cursor: pointer;
  padding: var(--sp-6);
  border-radius: var(--radius-md);
  transition: all calc(0.2s * var(--motion-scale));
  flex-shrink: 0;
  margin-top: var(--sp-1);
  margin-right: var(--sp-1);
}

.close-btn:hover {
  background: var(--color-border);
  color: var(--color-text-primary);
}

.close-btn svg {
  width: 18px;
  height: 18px;
}

/* ── Telefon (≤768): ekran genişliğine sığar; tür renginde ince kart (yalnız sunum,
   dokunma yine kapatır) ── */
@media (max-width: 768px) {
  .toast-item {
    width: 100%; padding: var(--sp-12) var(--sp-14); border-radius: var(--radius-lg);
    background: color-mix(in srgb, var(--toast-c) 14%, var(--color-bg-primary));
    border-color: color-mix(in srgb, var(--toast-c) 50%, transparent);
  }
  .toast-item:hover { transform: none; background: color-mix(in srgb, var(--toast-c) 14%, var(--color-bg-primary)); border-color: color-mix(in srgb, var(--toast-c) 50%, transparent); }
  .toast-icon-wrapper, .close-btn, .progress-bar { display: none; }
  .toast-text { padding-top: 0; }
  .toast-title { margin-bottom: var(--sp-2); font-size: calc(14px * var(--type-scale)); color: var(--toast-c); }
  .toast-message { font-size: calc(14px * var(--type-scale)); line-height: 1.4; color: var(--color-text-primary); }
}
</style>
